import type { CSSProperties } from 'react';

import { Z_INDEX } from '@/config/z-index';
import { getChildInlineStyle, toInlineStyle } from '@/features/_shared/lib/legacyStyle';

import PremiumFeatureMessage from './PremiumFeatureMessage';
import UpgradeToPremiumButton from './UpgradeToPremiumButton';
import type { ButtonType } from './UpgradeToPremiumButton';

type PremiumGateContentProps = {
  buttonSx?: unknown;
  buttonType?: ButtonType;
  messageSx?: unknown;
  showButton?: boolean;
  sx?: unknown;
  childStyle?: CSSProperties;
};

type InlinePremiumAccessGateProps = PremiumGateContentProps & {
  variant?: 'inline';
};

type PreviewPremiumAccessGateProps = PremiumGateContentProps & {
  backgroundImage?: string;
  backgroundRepeat?: string;
  backgroundSize?: string;
  blur?: string;
  contentSx?: unknown;
  height: string | number;
  imageTransform?: string;
  overlayColor?: string;
  variant: 'preview';
};

type AbsolutePremiumAccessGateProps = {
  backdropSx?: unknown;
  contentSx?: unknown;
  messageSx?: unknown;
  top?: string | number;
  variant: 'absolute';
};

type PremiumAccessGateProps =
  | InlinePremiumAccessGateProps
  | PreviewPremiumAccessGateProps
  | AbsolutePremiumAccessGateProps;

const previewImageUrl = "url('/img/preview/history-chart.png')";

const PremiumGateContent = ({
  buttonSx,
  buttonType = 'upgrade',
  messageSx,
  showButton = true,
  sx,
  childStyle
}: PremiumGateContentProps) => (
  <div
    className='flex flex-col items-center gap-2'
    style={toInlineStyle(sx)}
  >
    <div
      className='flex flex-col items-center'
      style={childStyle}
    >
      <PremiumFeatureMessage sx={messageSx} />
    </div>
    {showButton && (
      <UpgradeToPremiumButton
        sx={buttonSx}
        type={buttonType}
      />
    )}
  </div>
);

const PremiumAccessGate = (props: PremiumAccessGateProps) => {
  if (props.variant === 'absolute') {
    const { backdropSx, contentSx, messageSx, top = '20%' } = props;

    return (
      <>
        <div
          className='absolute inset-0 backdrop-blur-sm'
          style={{ zIndex: Z_INDEX.blurBackdrop, ...toInlineStyle(backdropSx) }}
        />
        <div
          className='absolute left-1/2 w-full -translate-x-1/2 text-center'
          style={{ top, zIndex: Z_INDEX.blurBackdropContent, ...toInlineStyle(contentSx) }}
        >
          <PremiumGateContent
            messageSx={messageSx}
            showButton={false}
          />
        </div>
      </>
    );
  }

  if (props.variant === 'preview') {
    const {
      backgroundImage = previewImageUrl,
      backgroundRepeat = 'no-repeat',
      backgroundSize = 'cover',
      blur = '4px',
      buttonSx,
      buttonType,
      contentSx,
      height,
      imageTransform = 'scale(1)',
      messageSx,
      overlayColor,
      showButton,
      sx
    } = props;

    return (
      <div
        className='relative overflow-hidden'
        style={toInlineStyle(sx)}
      >
        <div
          aria-hidden
          className='absolute inset-0'
          style={{
            backgroundImage,
            backgroundPosition: 'center',
            backgroundRepeat,
            backgroundSize,
            filter: `blur(${blur})`,
            transform: imageTransform,
            zIndex: Z_INDEX.base
          }}
        />
        <PremiumGateContent
          buttonSx={buttonSx}
          buttonType={buttonType}
          messageSx={messageSx}
          showButton={showButton}
          childStyle={getChildInlineStyle(contentSx)}
          sx={{
            position: 'relative',
            zIndex: Z_INDEX.raised,
            justifyContent: 'center',
            textAlign: 'center',
            backgroundColor: overlayColor,
            height,
            ...toInlineStyle(contentSx)
          }}
        />
      </div>
    );
  }

  const { buttonSx, buttonType, messageSx, showButton, sx } = props;

  return (
    <PremiumGateContent
      buttonSx={buttonSx}
      buttonType={buttonType}
      messageSx={messageSx}
      showButton={showButton}
      sx={{ justifyContent: 'center', textAlign: 'center', gap: '16px', ...toInlineStyle(sx) }}
    />
  );
};

export default PremiumAccessGate;
